import type { CellContext, ColumnDef } from '@tanstack/react-table'

import { Link } from '@/components'
import { HighlightText } from '@/components/ui/table/DataTable/HighlightText'
import {
  amountTone,
  cn,
  formatDate,
  humanizeText,
  numberToCurrency,
} from '@/utils'
import { formatActivityType, formatAssetClass } from '../../labels'
import { AccountTypeBadge } from '../AccountTypeBadge'
import type { ActivityRow, HoldingRow, RealizedRow, RowSource } from './types'

// Portfolio tables mirror the Transactions table. Every cell stays one line so all rows are the
// same height and the virtualized grid stays uniform.

// biome-ignore lint/suspicious/noExplicitAny: cell helpers are reused across the three row shapes.
type AnyCell = CellContext<any, unknown>

const searchQuery = (cell: AnyCell) => cell.table.options.meta?.searchQuery

const Dash = () => (
  <span aria-hidden="true" className="select-none text-muted-foreground">
    —
  </span>
)

const textCell = (cell: AnyCell) => {
  const value = cell.getValue() as string
  if (!value) return <Dash />
  return (
    <span className="block truncate text-blue-6">
      <HighlightText text={value} query={searchQuery(cell)} />
    </span>
  )
}

// Identifier columns (account number, ticker symbol) get a touch more weight than plain text.
const idCell = (cell: AnyCell) => {
  const value = cell.getValue() as string
  if (!value) return <Dash />
  return (
    <span className="block truncate font-medium text-blue-6">
      <HighlightText text={value} query={searchQuery(cell)} />
    </span>
  )
}

const dateCell = (cell: AnyCell) => {
  const value = cell.getValue() as string
  if (!value) return <Dash />
  return (
    <span className="block truncate text-blue-6">
      <HighlightText text={formatDate(value)} query={searchQuery(cell)} />
    </span>
  )
}

// A pill that humanizes an engine enum (asset class, activity type, realized term) and still
// highlights the search query.
const labeledPillCell =
  (format: (value: string) => string) => (cell: AnyCell) => {
    const value = cell.getValue() as string
    if (!value) return <Dash />
    return (
      <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-blue-6">
        <HighlightText text={format(value)} query={searchQuery(cell)} />
      </span>
    )
  }

// Use the same readable asset-class label as the allocation donut so table and chart agree.
const assetClassCell = labeledPillCell(formatAssetClass)
const activityTypeCell = labeledPillCell(formatActivityType)
const termCell = labeledPillCell(humanizeText)

const quantityCell = (cell: AnyCell) => {
  const value = cell.getValue() as number | null | undefined
  if (value == null) return <Dash />
  return (
    <span className="block truncate tabular-nums text-blue-6">
      {value.toLocaleString('en-US', { maximumFractionDigits: 4 })}
    </span>
  )
}

const pageCell = (cell: AnyCell) => {
  const value = cell.getValue() as number | null
  if (value == null) return <Dash />
  return (
    <span className="block truncate tabular-nums text-blue-6">{value}</span>
  )
}

// One money renderer for every currency column: `tone` colors gains/losses, `muted` greys a
// reference figure (cost basis), otherwise plain.
const moneyCell =
  ({ tone = false, muted = false } = {}) =>
  (cell: AnyCell) => {
    const value = cell.getValue() as number | null
    if (value == null) return <Dash />
    return (
      <span
        className={cn(
          'block truncate tabular-nums font-normal',
          tone
            ? amountTone(value)
            : muted
              ? 'text-muted-foreground'
              : 'text-blue-6',
        )}
      >
        <HighlightText
          text={numberToCurrency(value)}
          query={searchQuery(cell)}
        />
      </span>
    )
  }

const accountTypeCell = (cell: AnyCell) => {
  const type = cell.getValue() as ActivityRow['accountType']
  return (
    <AccountTypeBadge type={type}>
      <HighlightText text={type || ''} query={searchQuery(cell)} />
    </AccountTypeBadge>
  )
}

// Links a row back to its statement page: the file name opens the PDF at the exact page.
const documentCell = (cell: AnyCell) => {
  const source = cell.row.original as RowSource
  const fileName = cell.getValue() as string
  if (!fileName) return <Dash />
  if (source.statementFileUrl) {
    const href = source.sourcePage
      ? `${source.statementFileUrl}#page=${source.sourcePage}`
      : source.statementFileUrl
    return (
      <Link href={href} className="block truncate text-sm" theme="brand" newTab>
        <HighlightText text={fileName} query={searchQuery(cell)} />
      </Link>
    )
  }
  return (
    <span className="block truncate text-sm text-blue-6">
      <HighlightText text={fileName} query={searchQuery(cell)} />
    </span>
  )
}

const holdingGainLossCell = (cell: CellContext<HoldingRow, unknown>) => {
  const { gainLoss, costBasis } = cell.row.original
  if (gainLoss == null) return <Dash />
  // No percent without a cost basis: "+0.0%" would read as a real figure.
  const hasCostBasis = costBasis != null && costBasis > 0
  return (
    <span className={cn('block truncate tabular-nums', amountTone(gainLoss))}>
      {numberToCurrency(gainLoss)}
      {hasCostBasis && (
        <span className="ml-1 text-xs opacity-80">
          ({gainLoss >= 0 ? '+' : ''}
          {((gainLoss / costBasis) * 100).toFixed(1)}%)
        </span>
      )}
    </span>
  )
}

// Every tab leads with the account, since the dashboard reads across accounts at once. The masked
// number, the institution, and the type are each their own column.
const accountColumns = <Row,>(): ColumnDef<Row, unknown>[] => [
  {
    accessorKey: 'accountNumber',
    header: 'Account',
    size: 120,
    filterFn: 'custom' as never,
    cell: idCell,
  },
  {
    accessorKey: 'accountLabel',
    header: 'Institution',
    size: 160,
    cell: textCell,
  },
  {
    accessorKey: 'accountType',
    header: 'Account Type',
    size: 130,
    cell: accountTypeCell,
  },
]

// Document + page columns shared by all three tabs, appended last. Document is the one fluid
// column so it absorbs slack.
const sourceColumns = <Row,>(): ColumnDef<Row, unknown>[] => [
  {
    accessorKey: 'statementFileName',
    header: 'Document',
    size: 280,
    meta: { isFluid: true },
    cell: documentCell,
  },
  {
    accessorKey: 'sourcePage',
    header: 'Page',
    size: 90,
    cell: pageCell,
  },
]

export const holdingsColumns: ColumnDef<HoldingRow, unknown>[] = [
  ...accountColumns<HoldingRow>(),
  {
    accessorKey: 'symbol',
    header: 'Symbol',
    size: 90,
    filterFn: 'custom' as never,
    cell: idCell,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 280,
    filterFn: 'custom' as never,
    cell: textCell,
  },
  {
    accessorKey: 'assetClass',
    header: 'Asset class',
    size: 130,
    filterFn: 'custom' as never,
    cell: assetClassCell,
    meta: { exportValue: (value) => formatAssetClass(String(value)) },
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    size: 100,
    cell: quantityCell,
  },
  {
    accessorKey: 'price',
    header: 'Price',
    size: 110,
    cell: moneyCell(),
  },
  {
    accessorKey: 'marketValue',
    header: 'Market value',
    size: 130,
    filterFn: 'custom' as never,
    cell: moneyCell(),
  },
  {
    accessorKey: 'costBasis',
    header: 'Cost basis',
    size: 130,
    cell: moneyCell({ muted: true }),
  },
  {
    accessorKey: 'gainLoss',
    header: 'Gain / loss',
    size: 160,
    filterFn: 'custom' as never,
    cell: holdingGainLossCell,
  },
  ...sourceColumns<HoldingRow>(),
]

export const activityColumns: ColumnDef<ActivityRow, unknown>[] = [
  ...accountColumns<ActivityRow>(),
  {
    accessorKey: 'date',
    header: 'Date',
    size: 120,
    filterFn: 'custom' as never,
    cell: dateCell,
  },
  {
    accessorKey: 'type',
    header: 'Type',
    size: 140,
    filterFn: 'custom' as never,
    cell: activityTypeCell,
    meta: { exportValue: (value) => formatActivityType(String(value)) },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 280,
    filterFn: 'custom' as never,
    cell: textCell,
  },
  {
    accessorKey: 'quantity',
    header: 'Quantity',
    size: 110,
    cell: quantityCell,
  },
  {
    accessorKey: 'amount',
    header: 'Amount',
    size: 130,
    filterFn: 'custom' as never,
    cell: moneyCell({ tone: true }),
  },
  ...sourceColumns<ActivityRow>(),
]

export const realizedColumns: ColumnDef<RealizedRow, unknown>[] = [
  ...accountColumns<RealizedRow>(),
  {
    accessorKey: 'liquidationDate',
    header: 'Sale date',
    size: 120,
    filterFn: 'custom' as never,
    cell: dateCell,
  },
  {
    accessorKey: 'description',
    header: 'Description',
    size: 280,
    filterFn: 'custom' as never,
    cell: textCell,
  },
  {
    accessorKey: 'term',
    header: 'Term',
    size: 100,
    filterFn: 'custom' as never,
    cell: termCell,
    meta: { exportValue: (value) => humanizeText(String(value)) },
  },
  {
    accessorKey: 'saleAmount',
    header: 'Sale amount',
    size: 130,
    cell: moneyCell(),
  },
  {
    accessorKey: 'costBasis',
    header: 'Cost basis',
    size: 130,
    cell: moneyCell({ muted: true }),
  },
  {
    accessorKey: 'gainLoss',
    header: 'Gain / loss',
    size: 130,
    filterFn: 'custom' as never,
    cell: moneyCell({ tone: true }),
  },
  ...sourceColumns<RealizedRow>(),
]
